import { useEffect, useState } from 'react';
import { getSecretMessages } from 'api';

// Loads the current user's own private secret chat thread with the Game Master
// for a channel: fetches its history over REST, then merges in further secret
// messages pushed over the channel's WebSocket connection.
//
// This intentionally does not open its own WebSocket connection: `useChat` is
// the single owner of the channel's socket, so `incomingMessages` and
// `sendMessage` here are simply `useChat`'s `secretMessages` state and
// `sendSecretMessage` function, passed straight through by the caller (see
// ChatPage). That keeps this channel down to one physical connection instead
// of two.
export const useSecretChat = (channelId, incomingMessages, sendMessage) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!channelId) {
      setMessages([]);
      setLoading(false);
      return undefined;
    }

    let active = true;
    (async () => {
      try {
        setLoading(true);
        // Reset synchronously so nothing from the previous channel survives the merge.
        setMessages([]);
        const history = await getSecretMessages(channelId);
        if (active) {
          // `incomingMessages` (delivered over the socket) may already have grown
          // past this point by the time this history fetch resolves. Merge by ID
          // instead of replacing outright so none of those are lost.
          setMessages((prev) => {
            const merged = [...history];
            const knownIds = new Set(history.map((m) => m.id));
            for (const message of prev) {
              if (!knownIds.has(message.id)) {
                merged.push(message);
              }
            }
            return merged;
          });
        }
      } catch (e) {
        if (active) setError(e.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [channelId]);

  useEffect(() => {
    if (!incomingMessages.length) return;
    setMessages((prev) => {
      const knownIds = new Set(prev.map((m) => m.id));
      const additions = incomingMessages.filter((m) => !knownIds.has(m.id));
      return additions.length ? [...prev, ...additions] : prev;
    });
  }, [incomingMessages]);

  return { messages, loading, error, sendMessage };
};
